import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Chip, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Button, CircularProgress, InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  HistoryEdu as HistoryIcon,
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  FilterAltOutlined as FilterIcon,
  CheckCircleOutline as OkIcon,
  ErrorOutline as FailIcon,
  GroupsOutlined as PeopleIcon,
  TodayOutlined as TodayIcon,
  ShieldOutlined as ShieldIcon,
  BoltOutlined as BoltIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { auditLogs } from '../BaseURL';
import { AdminCard, AdminPageHead, EmptyState, TableWrap, BRAND, ADMIN, adminFieldSx } from './adminUi';

// ============================================================================
//  Activity Log — the Admin Panel's home
// ============================================================================
//
//  This is the first screen an administrator sees, so its job is not "list the
//  audit table". Its job is to answer, at a glance: is the system being used,
//  by whom, and is anything going wrong. The log itself is underneath, for
//  when the answer to one of those raises a question.
//
//  The headline numbers come from the server (GET /auditLogs → summary), not
//  from the hundred rows this page renders. Counting "actions today" by
//  filtering the returned page would report 100 on any busy day and be wrong
//  every single time.
//
//  On colour, two rules from the visualisation guidance are load-bearing here:
//
//    · The bar charts are ONE hue. They plot one series each — magnitude over
//      time, and magnitude by person — and a single series needs no palette
//      and no legend; the panel title names it.
//
//    · Success and failure are NEVER distinguished by colour alone. Apex green
//      (#2E9E4F) and the danger red (#D64545) are 2.2 ΔE apart under
//      deuteranopia — for a red-green colourblind reader, roughly 1 man in 12,
//      those two chips are the same colour. So every status chip carries an
//      icon and the word as well.

const MUTED  = BRAND.inkMuted;
const STRONG = BRAND.ink;
const BORDER = BRAND.line;

const LABEL_SX = {
  fontSize: 10.5,
  fontWeight: 800,
  color: BRAND.inkFaint,
  textTransform: 'uppercase',
  letterSpacing: '1px',
};

// Friendly labels for the activity_type codes the backend writes.
const TYPE_LABEL = {
  LOGIN:                'Login',
  LOGIN_FAILED:         'Failed login',
  LOGIN_BLOCKED:        'Login blocked',
  PATIENT_REGISTER:     'Patient registered',
  VITALS_SAVE:          'Vitals recorded',
  NOTE_SAVE:            'Nursing note',
  ADMISSION:            'Admission',
  DISCHARGE:            'Discharge',
  READMISSION:          'Re-admission',
  PATIENT_DECEASED:     'Patient deceased',
  BED_ASSIGN:           'Bed assigned',
  BED_TRANSFER:         'Bed/ward transfer',
  MED_REQUEST:          'Medication prescribed',
  MED_SERVE:            'Medication served',
  STOCK_ADJUST:         'Stock adjustment',
  PRODUCT_DELETE:       'Product deleted',
  PAYMENT:              'Payment processed',
  REVERSAL_REQUEST:     'Reversal requested',
  REVERSAL_APPROVE:     'Reversal approved',
  REVERSAL_REJECT:      'Reversal rejected',
  LAB_RESULT:           'Lab result saved',
  SCAN_RESULT:          'Scan result saved',
  LAB_REQUEST:          'Lab requested',
  SERVICE_REQUEST:      'Service requested',
  STAFF_CREATE:         'Staff created',
  USER_DELETE:          'User deleted',
  USER_STATUS:          'User status changed',
  PASSWORD_CHANGE:      'Password changed',
  SETTING_UPDATE:       'Setting updated',
  COMPLIANCE_FOLLOWUP:  'Compliance follow-up',
};

const friendlyType = (t) => TYPE_LABEL[t] || (t || '—');

const tonePerType = (t) => {
  if (!t) return { bg: '#F1F5F9', fg: '#475569' };
  if (t.startsWith('LOGIN_FAIL') || t === 'LOGIN_BLOCKED')      return { bg: '#FDECEC', fg: '#A93226' };
  if (t === 'DISCHARGE' || t === 'PATIENT_DECEASED')            return { bg: '#FDF3E3', fg: '#8A5A12' };
  if (t === 'LOGIN')                                            return { bg: BRAND.greenSoft, fg: BRAND.greenDark };
  if (t.startsWith('REVERSAL_REJECT') || t.endsWith('_DELETE')) return { bg: '#FDECEC', fg: '#A93226' };
  if (t === 'PAYMENT' || t === 'REVERSAL_APPROVE')              return { bg: '#E4F1FA', fg: '#1B5E8A' };
  return { bg: BRAND.navySoft, fg: BRAND.navy };
};

// Thousands separators, because "4601" and "46012" are the same shape at a
// glance and these numbers exist to be glanced at.
const fmt = (n) => Number(n || 0).toLocaleString('en-GB');

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ── Stat tile ───────────────────────────────────────────────────────────────
//
//  A single number is a stat tile, not a chart. The icon is tinted; the number
//  itself stays in ink — a figure painted in a status colour reads as a
//  warning even when it is only a count.

function StatTile({ icon: Icon, label, value, tint, hint }) {
  return (
    <Box
      sx={{
        background: BRAND.surface,
        border: `1px solid ${BORDER}`,
        borderRadius: ADMIN.radiusSm,
        px: { xs: 1.75, sm: 2 },
        py: { xs: 1.75, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 40, height: 40, borderRadius: '11px', flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: tint.bg,
        }}
      >
        <Icon sx={{ fontSize: 21, color: tint.fg }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: { xs: 20, sm: 23 }, fontWeight: 800, color: STRONG, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
        <Typography noWrap sx={{ fontSize: 11.5, color: MUTED, fontWeight: 600, mt: .25 }}>
          {label}
        </Typography>
        {hint && (
          <Typography noWrap sx={{ fontSize: 10.5, color: BRAND.inkFaint, mt: .1 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Activity over time ──────────────────────────────────────────────────────
//
//  One series, one hue, no legend. Bars are anchored to the baseline with 4px
//  rounded tops and a 2px gap; the count lives in the hover tooltip rather
//  than above every bar, which would be fourteen numbers nobody reads.
//
//  Empty days are drawn as a 2px stub rather than skipped. A quiet Sunday is
//  information — dropping it would silently compress the timeline and make a
//  gap look like continuous use.

function ActivityBars({ days }) {
  const max = Math.max(1, ...days.map(d => d.count));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: { xs: 96, sm: 120 } }}>
        {days.map((d) => {
          const pct = d.count > 0 ? Math.max(6, (d.count / max) * 100) : 0;
          return (
            <Tooltip
              key={d.date}
              arrow
              title={`${d.label} — ${fmt(d.count)} action${d.count === 1 ? '' : 's'}`}
            >
              <Box
                sx={{
                  flex: 1, minWidth: 0, height: '100%',
                  display: 'flex', alignItems: 'flex-end',
                  cursor: 'default',
                  // The hit target is the full column height, not the bar —
                  // a 3px stub on a quiet day is otherwise unhoverable.
                  '&:hover > div': { filter: 'brightness(1.25)' },
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: d.count > 0 ? `${pct}%` : '2px',
                    minHeight: 2,
                    borderRadius: '4px 4px 0 0',
                    background: d.count > 0 ? BRAND.navy : BORDER,
                    transition: 'filter .15s ease',
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Recessive axis: a hairline and the two endpoints. Fourteen date
          labels would not fit on a phone and would not be read on a desktop. */}
      <Box sx={{ borderTop: `1px solid ${BORDER}`, mt: .75, pt: .75, display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 10.5, color: BRAND.inkFaint }}>{days[0]?.label}</Typography>
        <Typography sx={{ fontSize: 10.5, color: BRAND.inkFaint }}>{days[days.length - 1]?.label}</Typography>
      </Box>
    </Box>
  );
}

// ── Most active people ──────────────────────────────────────────────────────
//
//  Magnitude by identity → horizontal bars, sorted, one hue. Six rows, so
//  every bar carries its value directly; there is no density problem to solve
//  and a tooltip would hide the numbers behind a hover.

function PeopleBars({ people }) {
  const max = Math.max(1, ...people.map(p => p.count));

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {people.map((p) => (
        <Box key={p.username} sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: .5 }}>
            <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: STRONG, minWidth: 0 }}>
              {p.username}
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {fmt(p.count)}
            </Typography>
          </Box>
          <Box sx={{ height: 7, borderRadius: '4px', background: BRAND.canvas, overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${Math.max(2, (p.count / max) * 100)}%`,
                borderRadius: '4px',
                background: BRAND.navy,
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Status chip ─────────────────────────────────────────────────────────────
//
//  Icon + word + colour. Never colour alone — see the note at the top of this
//  file about green and red being indistinguishable to a deuteranope.

function StatusChip({ status }) {
  const ok = (status || '').startsWith('success') || !status;
  return (
    <Chip
      size="small"
      icon={ok
        ? <OkIcon sx={{ fontSize: '13px !important', color: `${BRAND.greenDark} !important` }} />
        : <FailIcon sx={{ fontSize: '13px !important', color: '#A93226 !important' }} />}
      label={ok ? 'success' : (status || 'failed')}
      sx={{
        height: 20, fontSize: 10.5, fontWeight: 700,
        pl: .25,
        bgcolor: ok ? BRAND.greenSoft : '#FDECEC',
        color:   ok ? BRAND.greenDark : '#A93226',
        '& .MuiChip-label': { px: .75 },
      }}
    />
  );
}

const AuditLog = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);   // [{username, count}]
  const [types, setTypes] = useState([]);   // distinct activity_type values
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [usernameFilter, setUsernameFilter] = useState('');
  const [typeFilter,     setTypeFilter]     = useState('');
  const [from,           setFrom]           = useState('');
  const [to,             setTo]             = useState('');
  const [search,         setSearch]         = useState('');
  const [searchInput,    setSearchInput]    = useState('');

  // Debounce the free-text search by 300 ms so we don't fire on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (usernameFilter) params.username = usernameFilter;
      if (typeFilter)     params.activityType = typeFilter;
      if (from)           params.from   = from;
      if (to)             params.to     = to;
      if (search)         params.search = search;

      const { data } = await axios.get(auditLogs, { params, timeout: 15000 });
      if (data?.success) {
        setRows(data.results || []);
        setTotal(data.total || 0);
        setUsers(data.users || []);
        setTypes(data.types || []);
        // A backend that predates the summary block simply sends no `summary`.
        // The overview panels hide themselves rather than rendering zeros that
        // would read as "nobody is using the system".
        setSummary(data.summary || null);
      } else {
        console.warn('[activity log] backend returned success=false', data);
        setRows([]);
      }
    } catch (err) {
      console.error('[activity log] load failed:', err?.response?.status, err?.response?.data || err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [usernameFilter, typeFilter, from, to, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const u = r.username || '— unknown —';
      if (!map.has(u)) map.set(u, []);
      map.get(u).push(r);
    }
    return Array.from(map.entries()).map(([username, entries]) => ({ username, entries }));
  }, [rows]);

  // The server returns only days that HAVE entries. Filling the range with
  // zeros here is what makes a quiet stretch visible as a quiet stretch
  // rather than silently collapsing the timeline.
  const days = useMemo(() => {
    if (!summary?.byDay) return [];
    const found = new Map(summary.byDay.map(d => [d.date, d.count]));
    const out = [];
    const cursor = new Date();
    cursor.setHours(0, 0, 0, 0);
    cursor.setDate(cursor.getDate() - 13);
    for (let i = 0; i < 14; i++) {
      const key = ymd(cursor);
      out.push({
        date: key,
        count: found.get(key) || 0,
        label: cursor.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }, [summary]);

  const clearFilters = () => {
    setUsernameFilter(''); setTypeFilter(''); setFrom(''); setTo('');
    setSearch(''); setSearchInput('');
  };

  const hasFilter = usernameFilter || typeFilter || from || to || search;

  return (
    <Box>
      <AdminPageHead
        icon={HistoryIcon}
        title="Activity Log"
        subtitle="Who is using the system, and what they are doing"
        actions={
          <Tooltip title="Reload" arrow>
            <span>
              <IconButton
                size="small" onClick={fetchData} disabled={loading}
                aria-label="Reload activity"
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
              >
                {loading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      {/* ── Headline numbers ────────────────────────────────────────────── */}
      {summary && (
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 1.25, sm: 1.75 },
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            mb: { xs: 1.75, sm: 2.5 },
          }}
        >
          <StatTile
            icon={BoltIcon} label="Actions recorded" value={fmt(summary.total)}
            tint={{ bg: BRAND.navySoft, fg: BRAND.navy }}
            hint={hasFilter ? 'matching your filter' : 'all time'}
          />
          <StatTile
            icon={PeopleIcon} label="People active" value={fmt(summary.actors)}
            tint={{ bg: BRAND.navySoft, fg: BRAND.navy }}
            hint="distinct users"
          />
          <StatTile
            icon={TodayIcon} label="Today" value={fmt(summary.today)}
            tint={{ bg: BRAND.greenSoft, fg: BRAND.greenDark }}
            hint={`${fmt(summary.last7)} in the last 7 days`}
          />
          <StatTile
            icon={ShieldIcon} label="Failed or blocked" value={fmt(summary.failed)}
            tint={summary.failed > 0 ? { bg: '#FDECEC', fg: '#A93226' } : { bg: BRAND.navySoft, fg: BRAND.navy }}
            hint={summary.failed > 0 ? 'worth a look' : 'nothing rejected'}
          />
        </Box>
      )}

      {/* ── Two overview panels ─────────────────────────────────────────── */}
      {summary && (
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 1.75, sm: 2 },
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '1.6fr 1fr' },
            mb: { xs: 1.75, sm: 2.5 },
          }}
        >
          <AdminCard title="Activity over the last 14 days" subtitle="One bar per day">
            {days.length > 0
              ? <ActivityBars days={days} />
              : <Typography sx={{ fontSize: 13, color: MUTED, py: 3, textAlign: 'center' }}>
                  Nothing recorded in this period.
                </Typography>}
          </AdminCard>

          {/* The period is spelled out because this panel sits beside a
              14-day chart and counts over a different span — all time, unless
              a filter narrows it. Two adjacent panels on different periods
              with no label is a reliable way to be misread. */}
          <AdminCard
            title="Most active people"
            subtitle={hasFilter ? 'Matching your filter' : 'All time, by number of actions'}
          >
            {summary.topUsers?.length > 0
              ? <PeopleBars people={summary.topUsers} />
              : <Typography sx={{ fontSize: 13, color: MUTED, py: 3, textAlign: 'center' }}>
                  No named users yet.
                </Typography>}
          </AdminCard>
        </Box>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <AdminCard sx={{ mb: { xs: 1.75, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75, flexWrap: 'wrap' }}>
          <FilterIcon sx={{ color: BRAND.inkFaint, fontSize: 17 }} />
          <Typography sx={{ ...LABEL_SX, color: MUTED }}>Filter</Typography>
          {hasFilter && (
            <Button
              onClick={clearFilters}
              size="small"
              startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
              sx={{ ml: .5, textTransform: 'none', color: MUTED, fontWeight: 600, fontSize: 12 }}
            >
              Clear all
            </Button>
          )}
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}>
          <Autocomplete
            size="small"
            value={usernameFilter || null}
            onChange={(_, v) => setUsernameFilter(v || '')}
            options={users.map(u => u.username)}
            getOptionLabel={(opt) => {
              const u = users.find(x => x.username === opt);
              return u ? `${opt} (${u.count})` : opt;
            }}
            renderInput={(p) => (
              <TextField
                {...p}
                placeholder="Any user"
                label="User"
                sx={adminFieldSx}
                InputLabelProps={{ ...p.InputLabelProps, shrink: true }}
                InputProps={{
                  ...p.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start"><PersonIcon sx={{ fontSize: 17, color: BRAND.inkFaint }} /></InputAdornment>
                      {p.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            select
            size="small"
            label="Action type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={adminFieldSx}
            SelectProps={{ displayEmpty: true }}
            // Without shrink, `displayEmpty` renders "All actions" in the box
            // while the floating label sits unshrunk on top of it — the two
            // strings overlap and neither is readable. That is the collision
            // visible on the current screen.
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value=""><em>All actions</em></MenuItem>
            {types.map((t) => (
              <MenuItem key={t} value={t}>{friendlyType(t)}</MenuItem>
            ))}
          </TextField>

          <TextField
            size="small" type="date" label="From"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            sx={adminFieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: to || undefined }}
          />
          <TextField
            size="small" type="date" label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            sx={adminFieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: from || undefined }}
          />

          <TextField
            size="small"
            label="Search"
            placeholder="Search description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={adminFieldSx}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: BRAND.inkFaint }} /></InputAdornment>
              ),
              endAdornment: searchInput ? (
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}>
                  <ClearIcon sx={{ fontSize: 15 }} />
                </IconButton>
              ) : null,
            }}
          />
        </Box>
      </AdminCard>

      {/* ── Summary strip ───────────────────────────────────────────────── */}
      <Box sx={{ mb: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          Showing <strong style={{ color: STRONG }}>{fmt(rows.length)}</strong> of{' '}
          <strong style={{ color: STRONG }}>{fmt(total)}</strong> entries
        </Typography>
        {usernameFilter && (
          <Chip size="small" label={`User: ${usernameFilter}`} onDelete={() => setUsernameFilter('')}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: BRAND.navySoft, color: BRAND.navy }}
          />
        )}
        {typeFilter && (
          <Chip size="small" label={friendlyType(typeFilter)} onDelete={() => setTypeFilter('')}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: BRAND.navySoft, color: BRAND.navy }}
          />
        )}
        {(from || to) && (
          <Chip size="small"
            icon={<DateRangeIcon sx={{ fontSize: '13px !important', color: `${BRAND.navy} !important` }} />}
            label={`${from || '…'} → ${to || '…'}`}
            onDelete={() => { setFrom(''); setTo(''); }}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: BRAND.navySoft, color: BRAND.navy }}
          />
        )}
      </Box>

      {/* ── The log ─────────────────────────────────────────────────────── */}
      {/* No `overflow: hidden` on this container, deliberately. It would be the
          tidy way to clip children to the rounded corners — but `overflow`
          other than `visible` makes an element a scroll container, and a
          sticky child then sticks to THAT box instead of the viewport. Since
          this box never scrolls, the per-user headers below would simply never
          stick. The first header rounds its own top corners instead. */}
      <Box
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: ADMIN.radius,
          background: BRAND.surface,
        }}
      >
        {rows.length === 0 && !loading ? (
          <EmptyState
            icon={HistoryIcon}
            title="No activity to show"
            body={hasFilter
              ? 'Try adjusting the filters above.'
              : 'No actions have been logged yet. Activity will appear here as users interact with the system.'}
          />
        ) : (
          // No inner max-height/scroller here any more. The Admin Panel gives
          // this screen exactly one scrolling region; a second one nested
          // inside it produced the two side-by-side scrollbars, and made the
          // list scroll under the fingers of anyone trying to scroll the page.
          <Box>
            {grouped.map(({ username, entries }, gi) => (
              <Box key={username} sx={{ borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' } }}>
                {/* User group header. Sticks below the app bar rather than at
                    top:0 — the PAGE is the scroll container now, so top:0
                    would park it underneath the fixed AppBar where it cannot
                    be read. */}
                <Box
                  sx={{
                    position: 'sticky',
                    top: ADMIN.stickyTop,
                    zIndex: 2,
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1.1,
                    bgcolor: BRAND.navySoft,
                    borderBottom: `1px solid ${BORDER}`,
                    // Stands in for the clipping the container cannot do.
                    borderRadius: gi === 0 ? `${ADMIN.radius} ${ADMIN.radius} 0 0` : 0,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 16, color: BRAND.navy }} />
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: STRONG }}>
                    {username}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${entries.length} action${entries.length === 1 ? '' : 's'}`}
                    sx={{ ml: .5, height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: BRAND.surface, color: BRAND.navy }}
                  />
                </Box>

                <TableWrap sx={{ border: 'none', borderRadius: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['When', 'Action', 'Description', 'Target', 'Status', 'From'].map((h) => (
                            <TableCell key={h} sx={{ ...LABEL_SX, bgcolor: BRAND.canvas, py: 1, borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' }}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entries.map((r) => {
                          const tone = tonePerType(r.activity_type);
                          return (
                            <TableRow key={r.id} hover>
                              <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: MUTED, fontSize: 12, py: .85, borderBottom: `1px solid ${BRAND.canvas}`, whiteSpace: 'nowrap' }}>
                                {r.created_at}
                              </TableCell>
                              <TableCell sx={{ py: .85, borderBottom: `1px solid ${BRAND.canvas}` }}>
                                <Chip
                                  size="small"
                                  label={friendlyType(r.activity_type)}
                                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: tone.bg, color: tone.fg }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: .85, borderBottom: `1px solid ${BRAND.canvas}`, color: STRONG, fontSize: 12.5, maxWidth: 440, whiteSpace: 'normal' }}>
                                {r.description || <span style={{ color: BRAND.inkFaint }}>—</span>}
                              </TableCell>
                              <TableCell sx={{ py: .85, borderBottom: `1px solid ${BRAND.canvas}`, fontSize: 11.5, color: MUTED, whiteSpace: 'nowrap' }}>
                                {r.table_name
                                  ? <>{r.table_name}{r.record_id ? <> · <strong style={{ color: STRONG }}>#{r.record_id}</strong></> : null}</>
                                  : <span style={{ color: BRAND.inkFaint }}>—</span>}
                              </TableCell>
                              <TableCell sx={{ py: .85, borderBottom: `1px solid ${BRAND.canvas}` }}>
                                <StatusChip status={r.status} />
                              </TableCell>
                              <TableCell sx={{ py: .85, borderBottom: `1px solid ${BRAND.canvas}`, fontSize: 11.5, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                                {r.ip_address || <span style={{ color: BRAND.inkFaint }}>—</span>}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TableWrap>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AuditLog;
